import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDateTime } from '../lib/timeFormat';
import { followUpAPI, leadAPI, followUpSnooze } from '../api';
import { toast } from 'sonner';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths, startOfWeek, endOfWeek, isBefore } from 'date-fns';
import {
  ChevronLeft, ChevronRight, Phone, MessageSquare, Video, MapPin, Bell,
  Plus, X, Clock, CheckCircle, AlertCircle, Repeat, Snowflake
} from 'lucide-react';
import { CALENDAR } from '../constants/testIds';

const TYPES = [
  { key: 'call', label: 'Call', icon: Phone },
  { key: 'meeting', label: 'Meeting', icon: Video },
  { key: 'video_call', label: 'Video Call', icon: Video },
  { key: 'whatsapp', label: 'WhatsApp', icon: MessageSquare },
  { key: 'site_visit', label: 'Site Visit', icon: MapPin },
  { key: 'reminder', label: 'Reminder', icon: Bell },
  { key: 'quotation_followup', label: 'Quotation Follow-up', icon: Bell }
];

const RECURRING_OPTIONS = [
  { key: 'none', label: 'No Repeat' },
  { key: 'daily', label: 'Daily' },
  { key: 'weekly', label: 'Weekly' },
  { key: 'monthly', label: 'Monthly' }
];

export const CalendarPage = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [followUps, setFollowUps] = useState([]);
  const [leads, setLeads] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedFollowUp, setSelectedFollowUp] = useState(null);
  const [tabFilter, setTabFilter] = useState('all'); // all | today | missed | completed
  const [snoozeOpen, setSnoozeOpen] = useState(null); // holds fu.id
  const snoozeRef = useRef(null);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    lead_id: '',
    follow_up_type: 'call',
    scheduled_date: '',
    scheduled_time: '10:00',
    reminder_before: 30,
    recurring: 'none',
    recurring_end_date: '',
    notes: '',
    priority: 'medium'
  });

  const fetchData = useCallback(async () => {
    try {
      const [fuRes, leadsRes] = await Promise.all([
        followUpAPI.getAll(),
        leadAPI.getAll({ limit: 500 })
      ]);
      setFollowUps(fuRes.data);
      setLeads(leadsRes.data);
    } catch (error) {
      // Silent
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    const onClick = (e) => {
      if (snoozeRef.current && !snoozeRef.current.contains(e.target)) setSnoozeOpen(null);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const calendarStart = startOfWeek(monthStart);
  const calendarEnd = endOfWeek(monthEnd);
  const days = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

  const now = new Date();
  const isMissed = (fu) => !fu.completed && isBefore(new Date(fu.scheduled_date), now);

  const getStatusStyle = (fu) => {
    if (fu.completed) return { bg: 'bg-green-500', label: 'Completed', textColor: 'text-white' };
    if (isMissed(fu)) return { bg: 'bg-red-500', label: 'Missed', textColor: 'text-white' };
    if (isSameDay(new Date(fu.scheduled_date), now)) return { bg: 'bg-yellow-500', label: 'Today', textColor: 'text-white' };
    return { bg: 'bg-blue-500', label: 'Upcoming', textColor: 'text-white' };
  };

  const getTypeIcon = (type) => {
    const found = TYPES.find(t => t.key === type);
    const Icon = found?.icon || Bell;
    return <Icon className="h-3 w-3" />;
  };

  const getFollowUpsForDay = (day) => followUps.filter(fu => isSameDay(new Date(fu.scheduled_date), day));

  const filteredFollowUps = () => {
    if (tabFilter === 'today') return followUps.filter(fu => isSameDay(new Date(fu.scheduled_date), now) && !fu.completed);
    if (tabFilter === 'missed') return followUps.filter(isMissed);
    if (tabFilter === 'completed') return followUps.filter(fu => fu.completed);
    return followUps.filter(fu => !fu.completed).slice(0, 20);
  };

  const openCreate = () => {
    setSelectedFollowUp(null);
    setFormData({
      lead_id: '',
      follow_up_type: 'call',
      scheduled_date: format(new Date(), 'yyyy-MM-dd'),
      scheduled_time: '10:00',
      reminder_before: 30,
      recurring: 'none',
      recurring_end_date: '',
      notes: '',
      priority: 'medium'
    });
    setShowCreateModal(true);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!formData.lead_id) return toast.error('Please select a lead');
    try {
      const scheduled = new Date(`${formData.scheduled_date}T${formData.scheduled_time}:00`);
      const payload = {
        lead_id: formData.lead_id,
        follow_up_type: formData.follow_up_type,
        scheduled_date: scheduled.toISOString(),
        reminder_before: parseInt(formData.reminder_before) || 30,
        reminder_type: 'in_app',
        recurring: formData.recurring,
        recurring_end_date: formData.recurring_end_date ? new Date(formData.recurring_end_date).toISOString() : null,
        notes: formData.notes || null,
        priority: formData.priority
      };
      await followUpAPI.create(payload);
      toast.success('Follow-up scheduled');
      setShowCreateModal(false);
      fetchData();
    } catch (error) {
      const d = error.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Failed to create follow-up');
    }
  };

  const handleComplete = async (fu) => {
    try {
      await followUpAPI.update(fu.id, { completed: true, outcome: 'Completed' });
      toast.success('Follow-up marked complete');
      fetchData();
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const handleSnooze = async (fu, minutes) => {
    try {
      await followUpSnooze(fu.id, minutes);
      toast.success(`Snoozed for ${minutes >= 60 ? `${minutes / 60}h` : `${minutes}m`}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to snooze');
    }
  };

  const missedCount = followUps.filter(isMissed).length;
  const todayCount = followUps.filter(fu => isSameDay(new Date(fu.scheduled_date), now) && !fu.completed).length;
  const completedCount = followUps.filter(fu => fu.completed).length;

  return (
    <div className="space-y-6" data-testid={CALENDAR.container}>
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-4xl font-outfit font-semibold">Calendar & Follow-ups</h1>
          <p className="text-muted-foreground mt-2">Track reminders, meetings, and missed follow-ups</p>
        </div>
        <button
          onClick={openCreate}
          data-testid={CALENDAR.addFollowUpBtn}
          className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-white px-4 py-2 rounded-lg"
        >
          <Plus className="h-4 w-4" /> Schedule Follow-up
        </button>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="clay-card p-4 flex items-center gap-3 border-l-4 border-yellow-500">
          <Clock className="h-8 w-8 text-yellow-500" />
          <div>
            <p className="text-sm text-muted-foreground">Today</p>
            <p className="text-2xl font-outfit font-semibold">{todayCount}</p>
          </div>
        </div>
        <div className="clay-card p-4 flex items-center gap-3 border-l-4 border-red-500">
          <AlertCircle className="h-8 w-8 text-red-500" />
          <div>
            <p className="text-sm text-muted-foreground">Missed</p>
            <p className="text-2xl font-outfit font-semibold">{missedCount}</p>
          </div>
        </div>
        <div className="clay-card p-4 flex items-center gap-3 border-l-4 border-green-500">
          <CheckCircle className="h-8 w-8 text-green-500" />
          <div>
            <p className="text-sm text-muted-foreground">Completed</p>
            <p className="text-2xl font-outfit font-semibold">{completedCount}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Calendar Grid */}
        <div className="lg:col-span-3 clay-card p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-outfit font-medium">{format(currentDate, 'MMMM yyyy')}</h2>
            <div className="flex items-center gap-2">
              <button onClick={() => setCurrentDate(subMonths(currentDate, 1))} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg">
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button onClick={() => setCurrentDate(new Date())} className="px-4 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800">
                Today
              </button>
              <button onClick={() => setCurrentDate(addMonths(currentDate, 1))} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg">
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="grid grid-cols-7 gap-1 mb-2">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <div key={day} className="text-center text-xs font-medium text-muted-foreground py-2">{day}</div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-1">
            {days.map((day) => {
              const dayFollowUps = getFollowUpsForDay(day);
              const isCurrentMonth = isSameMonth(day, currentDate);
              const isToday = isSameDay(day, new Date());

              return (
                <div key={day.toISOString()} className={`min-h-[100px] p-2 border border-slate-200 dark:border-slate-700 rounded-lg ${
                  isCurrentMonth ? 'bg-white dark:bg-slate-900' : 'bg-slate-50 dark:bg-slate-800/50 opacity-50'
                } ${isToday ? 'ring-2 ring-primary' : ''}`}>
                  <p className={`text-sm font-medium mb-1 ${isToday ? 'text-primary' : ''}`}>
                    {format(day, 'd')}
                  </p>
                  <div className="space-y-1">
                    {dayFollowUps.slice(0, 2).map((fu) => {
                      const s = getStatusStyle(fu);
                      return (
                        <div
                          key={fu.id}
                          onClick={() => setSelectedFollowUp(fu)}
                          data-testid={`${CALENDAR.followUpItem}-${fu.id}`}
                          className={`${s.bg} ${s.textColor} text-xs px-2 py-1 rounded flex items-center gap-1 cursor-pointer hover:opacity-90`}
                        >
                          {getTypeIcon(fu.follow_up_type)}
                          <span className="truncate">{fu.lead_name}</span>
                        </div>
                      );
                    })}
                    {dayFollowUps.length > 2 && (
                      <p className="text-xs text-muted-foreground">+{dayFollowUps.length - 2} more</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Legend */}
          <div className="mt-4 flex flex-wrap gap-3 text-xs">
            <div className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-yellow-500"></div>Today</div>
            <div className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-blue-500"></div>Upcoming</div>
            <div className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-red-500"></div>Missed</div>
            <div className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-green-500"></div>Completed</div>
          </div>
        </div>

        {/* Side Panel */}
        <div className="space-y-4">
          <div className="clay-card p-4">
            <div className="flex gap-1 mb-3 text-xs">
              {['all', 'today', 'missed', 'completed'].map(t => (
                <button
                  key={t}
                  onClick={() => setTabFilter(t)}
                  data-testid={`calendar-tab-${t}`}
                  className={`px-2 py-1 rounded capitalize ${tabFilter === t ? 'bg-primary text-white' : 'bg-slate-100 dark:bg-slate-800'}`}
                >
                  {t}
                </button>
              ))}
            </div>
            <div className="space-y-2 max-h-[500px] overflow-y-auto">
              {filteredFollowUps().length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">No follow-ups</p>
              ) : (
                filteredFollowUps().map((fu) => {
                  const s = getStatusStyle(fu);
                  return (
                    <div key={fu.id} className="border border-slate-200 dark:border-slate-700 rounded-lg p-3">
                      <div className="flex items-start gap-2">
                        <div className={`p-1.5 rounded ${s.bg} text-white`}>
                          {getTypeIcon(fu.follow_up_type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <button
                            onClick={() => navigate(`/leads/${fu.lead_id}`)}
                            className="text-sm font-medium truncate text-left w-full hover:underline"
                          >
                            {fu.lead_name}
                          </button>
                          <p className="text-xs text-muted-foreground">
                            {formatDateTime(fu.scheduled_date)}
                          </p>
                          <div className="flex items-center gap-1 mt-1 flex-wrap">
                            <span className={`inline-block px-1.5 py-0.5 text-[10px] rounded ${s.bg} text-white`}>{s.label}</span>
                            {fu.recurring && fu.recurring !== 'none' && (
                              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] bg-slate-200 dark:bg-slate-700 rounded">
                                <Repeat className="h-2.5 w-2.5" /> {fu.recurring}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      {!fu.completed && (
                        <div className="flex gap-1 mt-2 pt-2 border-t border-slate-200 dark:border-slate-700">
                          <button
                            onClick={() => handleComplete(fu)}
                            data-testid={`${CALENDAR.completeBtn}-${fu.id}`}
                            className="flex-1 text-xs px-2 py-1 bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 rounded hover:bg-green-200 flex items-center justify-center gap-1"
                          >
                            <CheckCircle className="h-3 w-3" /> Done
                          </button>
                          <div className="relative" ref={snoozeOpen === fu.id ? snoozeRef : null}>
                            <button
                              onClick={() => setSnoozeOpen(snoozeOpen === fu.id ? null : fu.id)}
                              data-testid={`${CALENDAR.snoozeBtn}-${fu.id}`}
                              className="text-xs px-2 py-1 bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400 rounded hover:bg-yellow-200 flex items-center gap-1"
                            >
                              <Snowflake className="h-3 w-3" /> Snooze
                            </button>
                            {snoozeOpen === fu.id && (
                              <div className="absolute right-0 top-full mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg z-10 min-w-[120px]">
                                {[10, 30, 60, 240, 1440].map(m => (
                                  <button
                                    key={m}
                                    onClick={() => { handleSnooze(fu, m); setSnoozeOpen(null); }}
                                    data-testid={`calendar-snooze-${fu.id}-${m}`}
                                    className="w-full text-left px-3 py-1.5 text-xs hover:bg-slate-100 dark:hover:bg-slate-700"
                                  >
                                    {m === 1440 ? '1 day' : m >= 60 ? `${m / 60}h` : `${m}m`}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowCreateModal(false)}>
          <div className="bg-white dark:bg-[#131A2B] rounded-xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-outfit font-semibold">Schedule Follow-up</h2>
              <button onClick={() => setShowCreateModal(false)}><X className="h-5 w-5" /></button>
            </div>
            <form onSubmit={handleCreate} className="space-y-3">
              <div>
                <label className="block text-sm font-medium mb-1">Lead *</label>
                <select
                  required
                  data-testid="followup-lead-select"
                  value={formData.lead_id}
                  onChange={(e) => setFormData({ ...formData, lead_id: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-sm"
                >
                  <option value="">Select a lead...</option>
                  {leads.map(l => <option key={l.id} value={l.id}>{l.customer_name} — {l.primary_mobile}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">Type</label>
                  <select
                    value={formData.follow_up_type}
                    onChange={(e) => setFormData({ ...formData, follow_up_type: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-sm"
                  >
                    {TYPES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Priority</label>
                  <select
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-sm"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">Date *</label>
                  <input
                    required
                    type="date"
                    data-testid="followup-date-input"
                    value={formData.scheduled_date}
                    onChange={(e) => setFormData({ ...formData, scheduled_date: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Time *</label>
                  <input
                    required
                    type="time"
                    data-testid="followup-time-input"
                    value={formData.scheduled_time}
                    onChange={(e) => setFormData({ ...formData, scheduled_time: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-sm"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">Reminder (mins before)</label>
                  <input
                    type="number"
                    min="0"
                    value={formData.reminder_before}
                    onChange={(e) => setFormData({ ...formData, reminder_before: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Recurring</label>
                  <select
                    value={formData.recurring}
                    onChange={(e) => setFormData({ ...formData, recurring: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-sm"
                  >
                    {RECURRING_OPTIONS.map(r => <option key={r.key} value={r.key}>{r.label}</option>)}
                  </select>
                </div>
              </div>
              {formData.recurring !== 'none' && (
                <div>
                  <label className="block text-sm font-medium mb-1">Recurring End Date</label>
                  <input
                    type="date"
                    value={formData.recurring_end_date}
                    onChange={(e) => setFormData({ ...formData, recurring_end_date: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-sm"
                  />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium mb-1">Notes</label>
                <textarea
                  rows={2}
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-sm"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setShowCreateModal(false)} className="px-4 py-2 border border-slate-200 dark:border-slate-700 rounded-lg text-sm">Cancel</button>
                <button type="submit" data-testid="followup-submit" className="px-4 py-2 bg-primary text-white rounded-lg text-sm">Schedule</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {selectedFollowUp && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedFollowUp(null)}>
          <div className="bg-white dark:bg-[#131A2B] rounded-xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-outfit font-semibold">Follow-up Details</h2>
              <button onClick={() => setSelectedFollowUp(null)}><X className="h-5 w-5" /></button>
            </div>
            <div className="space-y-2 text-sm">
              <p><span className="text-muted-foreground">Lead: </span><button onClick={() => { navigate(`/leads/${selectedFollowUp.lead_id}`); setSelectedFollowUp(null); }} className="text-primary hover:underline">{selectedFollowUp.lead_name}</button></p>
              <p><span className="text-muted-foreground">Type: </span>{selectedFollowUp.follow_up_type}</p>
              <p><span className="text-muted-foreground">Scheduled: </span>{format(new Date(selectedFollowUp.scheduled_date), 'MMM d, yyyy h:mm a')}</p>
              {selectedFollowUp.notes && <p><span className="text-muted-foreground">Notes: </span>{selectedFollowUp.notes}</p>}
              <p><span className="text-muted-foreground">Status: </span>{selectedFollowUp.completed ? 'Completed' : (isMissed(selectedFollowUp) ? 'Missed' : 'Pending')}</p>
              {selectedFollowUp.recurring && selectedFollowUp.recurring !== 'none' && (
                <p><span className="text-muted-foreground">Recurring: </span>{selectedFollowUp.recurring}</p>
              )}
            </div>
            {!selectedFollowUp.completed && (
              <div className="flex gap-2 mt-4">
                <button
                  onClick={() => { handleComplete(selectedFollowUp); setSelectedFollowUp(null); }}
                  className="flex-1 px-3 py-2 bg-green-500 text-white rounded-lg text-sm"
                >
                  Mark Complete
                </button>
                <button
                  onClick={() => { handleSnooze(selectedFollowUp, 60); setSelectedFollowUp(null); }}
                  className="flex-1 px-3 py-2 bg-yellow-500 text-white rounded-lg text-sm"
                >
                  Snooze 1h
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
